// /client/src/pages/Admin/DashboardAdherents/GenderAgeDistributionChart.jsx
import React, { useEffect, useMemo, useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import {
  getGenderDistribution,
  getAgeDistribution,
} from "../../../services/dashboardServices";

/* Couleurs cohérentes avec la charte */
const GENRE_COLORS = {
  Masculin: "#4F46E5", // indigo-600
  Féminin: "#EC4899", // pink-500
  "Non renseigné": "#94A3B8", // slate-400
};
const AGE_COLORS = {
  "-18": "#06B6D4", // cyan-500
  "18+": "#10B981", // emerald-500
  "Non renseigné": "#94A3B8",
};

function Dot({ color }) {
  return (
    <span
      className="inline-block h-2.5 w-2.5 rounded-full"
      style={{ backgroundColor: color }}
    />
  );
}

function Skeleton() {
  return (
    <div className="rounded-2xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-900/60 backdrop-blur p-4 sm:p-5">
      <div className="h-4 w-56 bg-slate-200 dark:bg-slate-800 animate-pulse rounded mb-4" />
      <div className="h-[320px] rounded-xl bg-slate-100 dark:bg-slate-800 animate-pulse" />
      <div className="mt-4 grid grid-cols-2 gap-3">
        {[...Array(4)].map((_, i) => (
          <div
            key={i}
            className="h-3 bg-slate-200 dark:bg-slate-800 animate-pulse rounded"
          />
        ))}
      </div>
    </div>
  );
}

export default function GenderAgeDistributionChart() {
  const [gender, setGender] = useState([]); // [{ name, value }]
  const [age, setAge] = useState([]); // [{ name, value }]
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);

        const [g, a] = await Promise.all([
          getGenderDistribution(), // -> [{ genre: "Masculin", count: 2 }, ...]
          getAgeDistribution(), // -> [{ age: "-18", count: 1 }, ...]
        ]);

        if (!alive) return;

        const genderData = (Array.isArray(g) ? g : []).map((x) => ({
          name: x?.genre || "Non renseigné",
          value: Number(x?.count || 0),
          color: GENRE_COLORS[x?.genre] || GENRE_COLORS["Non renseigné"],
        }));

        const ageData = (Array.isArray(a) ? a : []).map((x) => ({
          name: x?.age || "Non renseigné",
          value: Number(x?.count || 0),
          color: AGE_COLORS[x?.age] || AGE_COLORS["Non renseigné"],
        }));

        setGender(genderData);
        setAge(ageData);
      } catch (e) {
        console.error("Erreur répartition genre/âge :", e);
        setGender([]);
        setAge([]);
      } finally {
        alive && setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  const total = useMemo(
    () =>
      gender.reduce((s, x) => s + x.value, 0) ||
      age.reduce((s, x) => s + x.value, 0) ||
      0,
    [gender, age]
  );

  if (loading) return <Skeleton />;

  const hasData =
    (gender?.length && gender.some((x) => x.value > 0)) ||
    (age?.length && age.some((x) => x.value > 0));

  if (!hasData) {
    return (
      <section className="rounded-2xl overflow-hidden ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-900/60 backdrop-blur p-4 sm:p-5">
        <h3 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100">
          Répartition globale — genre & âge
        </h3>
        <p className="mt-4 text-center text-slate-600 dark:text-slate-400">
          Aucune donnée à afficher.
        </p>
      </section>
    );
  }

  return (
    <section className="rounded-2xl overflow-hidden ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-900/60 backdrop-blur p-4 sm:p-5">
      {/* Titre */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100">
            Répartition globale — genre & âge
          </h3>
          <div className="text-[11px] text-slate-500 dark:text-slate-400">
            Total adhérents&nbsp;: {total.toLocaleString("fr-FR")}
          </div>
        </div>
      </div>

      {/* Graphiques */}
      <div className="w-full">
        {/* Version desktop/tablette : 2 donuts côte à côte */}
        <div className="hidden sm:block" style={{ height: 340 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              {/* GENRE */}
              <Pie
                data={gender}
                dataKey="value"
                nameKey="name"
                cx="28%"
                cy="50%"
                outerRadius={110}
                innerRadius={60}
                paddingAngle={2}
                label={({ name, percent }) =>
                  `${name} ${(percent * 100).toFixed(0)}%`
                }
                labelLine={false}
              >
                {gender.map((entry, i) => (
                  <Cell key={`g-${i}`} fill={entry.color} />
                ))}
              </Pie>

              {/* AGE */}
              <Pie
                data={age}
                dataKey="value"
                nameKey="name"
                cx="72%"
                cy="50%"
                outerRadius={110}
                innerRadius={60}
                paddingAngle={2}
                label={({ name, value }) => `${name} : ${value}`}
                labelLine={false}
              >
                {age.map((entry, i) => (
                  <Cell key={`a-${i}`} fill={entry.color} />
                ))}
              </Pie>

              <Tooltip
                contentStyle={{
                  backgroundColor: "var(--tw-prose-invert, #0f172a)",
                  border: "none",
                  color: "#fff",
                  borderRadius: 12,
                }}
                itemStyle={{ color: "#fff" }}
                labelStyle={{ color: "#fff" }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Version mobile : 2 donuts l’un sous l’autre */}
        <div className="block sm:hidden space-y-4">
          <div style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={gender}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  innerRadius={58}
                  paddingAngle={2}
                  label={({ name, percent }) =>
                    `${name} ${(percent * 100).toFixed(0)}%`
                  }
                  labelLine={false}
                >
                  {gender.map((entry, i) => (
                    <Cell key={`gm-${i}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: "var(--tw-prose-invert, #0f172a)",
                    border: "none",
                    color: "#fff",
                    borderRadius: 12,
                  }}
                  itemStyle={{ color: "#fff" }}
                  labelStyle={{ color: "#fff" }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={age}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  innerRadius={58}
                  paddingAngle={2}
                  label={({ name, value }) => `${name} : ${value}`}
                  labelLine={false}
                >
                  {age.map((entry, i) => (
                    <Cell key={`am-${i}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: "var(--tw-prose-invert, #0f172a)",
                    border: "none",
                    color: "#fff",
                    borderRadius: 12,
                  }}
                  itemStyle={{ color: "#fff" }}
                  labelStyle={{ color: "#fff" }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Légendes compactes */}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-3 bg-slate-50 dark:bg-slate-900/40">
          <div className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
            Genre
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm">
            {gender.map((g) => (
              <div key={g.name} className="flex items-center gap-2">
                <Dot color={g.color} />
                <span className="text-slate-700 dark:text-slate-200">
                  {g.name}
                </span>
                <span className="text-slate-500 dark:text-slate-400">
                  ({g.value})
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-3 bg-slate-50 dark:bg-slate-900/40">
          <div className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
            Tranche d’âge
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm">
            {age.map((a) => (
              <div key={a.name} className="flex items-center gap-2">
                <Dot color={a.color} />
                <span className="text-slate-700 dark:text-slate-200">
                  {a.name}
                </span>
                <span className="text-slate-500 dark:text-slate-400">
                  ({a.value})
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
